/**
 * Wholesale portal API client.
 * All API calls are prefixed with /api/v1/wholesale/.
 */

import type {
  WholesaleProduct,
  WholesaleInvoice,
  WholesaleAccountStatement,
  WholesaleOrder,
  OrderGuide,
  OrderGuideDetail,
  WholesaleCartItem,
} from '@provendoro/shared/types'

interface ApiResponse<T> {
  data?: T
  error?: { message: string; code: string }
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

async function apiFetch<T>(
  url: string,
  options?: RequestInit,
): Promise<ApiResponse<T>> {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })
  return res.json() as Promise<ApiResponse<T>>
}

// ── Account ──

export async function getWholesaleAccount(): Promise<ApiResponse<WholesaleAccountStatement['account'] & {
  recentInvoices: WholesaleInvoice[]
  recentOrders: Array<{ id: string; status: string; totalCents: number; createdAt: string; itemCount: number }>
}>> {
  const res = await apiFetch<WholesaleAccountStatement>('/api/v1/wholesale/account')
  if (res.error) return res as ApiResponse<never>
  const data = res.data
  if (!data) return { error: { message: 'No data', code: 'NO_DATA' } }
  return {
    data: {
      ...data.account,
      recentInvoices: data.recentInvoices,
      recentOrders: data.recentOrders,
    },
  }
}

// ── Products ──

export interface WholesaleProductsResult {
  products: WholesaleProduct[]
  pagination: Pagination
}

export async function listWholesaleProducts(params?: {
  categoryId?: string
  search?: string
  page?: number
  limit?: number
}): Promise<ApiResponse<WholesaleProductsResult>> {
  const q = new URLSearchParams()
  if (params?.categoryId) q.set('categoryId', params.categoryId)
  if (params?.search) q.set('search', params.search)
  if (params?.page) q.set('page', String(params.page))
  if (params?.limit) q.set('limit', String(params.limit))

  const qs = q.toString()
  const res = await apiFetch<WholesaleProduct[]>(
    `/api/v1/wholesale/products${qs ? `?${qs}` : ''}`,
  )

  if (res.error) return res as unknown as ApiResponse<WholesaleProductsResult>
  // The endpoint wraps products in { data, pagination }
  const raw = res as unknown as { data: WholesaleProduct[]; pagination: Pagination }
  return {
    data: {
      products: raw.data ?? [],
      pagination: raw.pagination ?? { page: 1, limit: 20, total: 0, totalPages: 0 },
    },
  }
}

// ── Order Guides ──

export async function listOrderGuides(): Promise<ApiResponse<OrderGuide[]>> {
  return apiFetch<OrderGuide[]>('/api/v1/wholesale/order-guides')
}

export async function getOrderGuide(id: string): Promise<ApiResponse<OrderGuideDetail>> {
  return apiFetch<OrderGuideDetail>(`/api/v1/wholesale/order-guides/${id}`)
}

// ── Orders ──

export async function listWholesaleOrders(): Promise<ApiResponse<WholesaleOrder[]>> {
  return apiFetch<WholesaleOrder[]>('/api/v1/wholesale/orders')
}

export async function getWholesaleOrder(id: string): Promise<ApiResponse<WholesaleOrder>> {
  return apiFetch<WholesaleOrder>(`/api/v1/wholesale/orders/${id}`)
}

export async function reorderFromOrder(orderId: string): Promise<ApiResponse<{
  brandId: string
  items: WholesaleCartItem[]
  skippedCount: number
}>> {
  return apiFetch(`/api/v1/wholesale/orders/${orderId}/reorder`, {
    method: 'POST',
  })
}

export async function placeWholesaleOrder(input: {
  brandId: string
  items: Array<{ productId: string; quantity: number; unitType: string; unitPriceCents: number }>
  deliveryDate?: string
  poNumber?: string
  notes?: string
}): Promise<ApiResponse<{ order: { id: string; status: string; totalCents: number }; invoiceId: string | null }>> {
  return apiFetch('/api/v1/wholesale/orders', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

// ── Invoices ──

export async function listWholesaleInvoices(): Promise<ApiResponse<WholesaleInvoice[]>> {
  return apiFetch<WholesaleInvoice[]>('/api/v1/wholesale/invoices')
}

export async function getWholesaleInvoice(id: string): Promise<ApiResponse<WholesaleInvoice>> {
  return apiFetch<WholesaleInvoice>(`/api/v1/wholesale/invoices/${id}`)
}

export async function payWholesaleInvoice(id: string): Promise<ApiResponse<{
  paymentUrl?: string
  clientSecret?: string
  paymentIntentId?: string
  amountCents?: number
  method: 'stripe_hosted' | 'payment_intent'
}>> {
  return apiFetch(`/api/v1/wholesale/invoices/${id}/pay`, {
    method: 'POST',
  })
}
